import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { BookCollectionFormType } from '@models/bookCollection/BookCollectionFormType'
import { BookCollectionType } from '@models/bookCollection/BookCollectionType'
import { useBookCollectionAction } from '@hooks/bookCollection/useBookCollectionAction'
import { BookCollectionListProps, PrivacyType } from './interface'

export function withBookCollectionList(
  Component: React.FC<BookCollectionListProps>
) {
  function WithBookCollectionList() {
    const bookCollectionClient = useBookCollectionAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const router = useRouter()
    const { isAuthenticated, isUserLoading } = useAuthentication()
    const [privacyType, setPrivacyType] = useState<PrivacyType>(
      (router.query.collectionType as PrivacyType) || null
    )
    const manageCollectionList = useModal(
      {
        modal: 'manageCollectionList',
      },
      true
    )
    const confirmModal = useModal(
      {
        modal: 'confirm',
      },
      true
    )

    const { data: bookCollections = [], isLoading } = useQuery(
      ['my-book-collection-list', privacyType],
      () => bookCollectionClient.getBookCollections({ privacyType }),
      {
        enabled: isAuthenticated,
      }
    )

    function handlePrivacyTypeChange(value: PrivacyType | null) {
      setPrivacyType(value)
      const query: any = {}

      if (router.query.tab) {
        query.tab = router.query.tab
      }

      if (value) {
        query.collectionType = value
      }

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        {
          scroll: false,
        }
      )
    }

    function handleCreateBookCollection() {
      manageCollectionList.show({
        title: 'สร้างลิสต์',
        onSubmit: async (values: BookCollectionFormType) => {
          try {
            await bookCollectionClient.createBookCollection(values)
            await queryClient.refetchQueries(
              ['my-book-collection-list', privacyType],
              { active: true }
            )
            alert.success('สร้างลิสต์หนังสือสำเร็จ')
          } catch (error) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถสร้างลิสต์หนังสือได้')
          } finally {
            manageCollectionList.hide()
          }
        },
      })
    }

    function handleEditBookCollection(bookCollection: BookCollectionType) {
      manageCollectionList.show({
        title: 'แก้ไขลิสต์',
        initialValues: bookCollection,
        onSubmit: async (values: BookCollectionFormType) => {
          try {
            await bookCollectionClient.updateBookCollection(values)
            await queryClient.refetchQueries(
              ['my-book-collection-list', privacyType],
              { active: true }
            )
            alert.success('แก้ไขลิสต์หนังสือสำเร็จ')
          } catch (error) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถแก้ไขลิสต์หนังสือได้')
          } finally {
            manageCollectionList.hide()
          }
        },
      })
    }

    function handleDeleteBookCollection(id: number, name: string) {
      confirmModal.show({
        content: (
          <div className='grid w-[400px] px-[20px] gap-y-[10px] mb-[20px] mobile:w-[248px] font-mitr text-center text-[24px] text-secondary dark:text-white-2 font-medium'>
            <p>ยืนยันการลบลิสต์หนังสือหรือไม่</p>
            <p className='text-[16px] text-secondary-1 dark:text-dark-10'>
              หากลบแล้วจะไม่สามารถกู้คืนได้
            </p>
          </div>
        ),
        onConfirm: async () => {
          try {
            const response = await bookCollectionClient.removeBookCollection(id)
            if (response) {
              queryClient.setQueryData<BookCollectionType[] | undefined>(
                ['my-book-collection-list', privacyType],
                (oldData?: BookCollectionType[]) => {
                  if (oldData) {
                    return oldData.filter(item => item.id !== response.id)
                  }
                  return oldData
                }
              )
              alert.success('ลบลิสต์หนังสือสำเร็จ')
            }
          } catch (error) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถลบลิสต์หนังสือได้')
          } finally {
            confirmModal.hide()
          }
        },
        onClose: () => {
          confirmModal.hide()
        },
      })
    }

    function handleGoToBookCollectionDetail(id: number) {
      router.push(`/my-reading/book-list/collection/${id}`)
    }

    const componentProps = {
      privacyType,
      bookCollections,
      handlePrivacyTypeChange,
      handleCreateBookCollection,
      handleEditBookCollection,
      handleDeleteBookCollection,
      handleGoToBookCollectionDetail,
      isLoading: isLoading || isUserLoading,
    }
    return <Component {...componentProps} />
  }

  return WithBookCollectionList
}
